import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useRef, useState } from 'react';
import { ImagePlus, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UploadControlProps {
  onFileSelected: (file: File) => void;
  onClear?: () => void;
  preview?: string | null;
  label?: string;
  accept?: string;
  maxSize?: number; // in MB
  className?: string;
  isLoading?: boolean;
  isActive?: boolean;
}

export const UploadControl: React.FC<UploadControlProps> = ({
  onFileSelected,
  onClear,
  preview,
  label = translateInterfaceText("Upload file"),
  accept = "image/*",
  maxSize = 20, // default 20MB
  className,
  isLoading = false,
  isActive = false
}) => {
  useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (file: File) => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(translateInterfaceText("Arquivo excede o limite de {value0}MB", { value0: (maxSize) }));
      return;
    }
    
    setError(null);
    onFileSelected(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (inputRef.current) {
      // Zera o value antes de abrir o seletor: sem isso, reescolher o mesmo
      // arquivo não dispara o onChange e o upload não é reenviado.
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {label && <label className="text-[13px] font-semibold text-[#1A1A1A]">{label}</label>}
        <div className="border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center bg-[#F6F9FF] border-[#CBD5E1]/40 min-h-[160px]">
          <div className="w-12 h-12 rounded-xl bg-[#1D4FFF]/10 flex items-center justify-center mb-3">
            <Loader2 className="h-5 w-5 text-[#1D4FFF] animate-spin" />
          </div>
          <p className="text-[13px] text-[#6B7280]">{translateInterfaceText("Carregando...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && <label className="text-[13px] font-semibold text-[#1A1A1A]">{label}</label>}
      
      {preview ? (
        /* Preview com overlay de ações */
        <div 
          className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#E4E7EC] group cursor-pointer"
        >
          <img
            src={preview}
            alt={translateInterfaceText("Preview")}
            className="w-full h-full object-contain transition-all duration-200 group-hover:brightness-75"
          />
          
          {/* Badge Ativo */}
          {isActive && (
            <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-[#10B981]/10 text-[#059669] rounded-full border border-[#10B981]/20 z-10">
              {translateInterfaceText("Ativo")}</span>
          )}
          
          {/* Overlay com ações — aparece no hover */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-6 z-20 rounded-xl">
            {/* Botão Trocar */}
            <button
              type="button"
              onClick={handleClick}
              className="flex flex-col items-center gap-2 group/btn"
            >
              <div className="w-11 h-11 rounded-full bg-[#1D4FFF] flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                <RefreshCw className="h-5 w-5 text-white" />
              </div>
              <span className="text-white text-[12px] font-medium">{translateInterfaceText("Trocar")}</span>
            </button>

            {/* Botão Remover */}
            {onClear && (
              <button
                type="button"
                onClick={handleRemove}
                className="flex flex-col items-center gap-2 group/btn"
              >
                <div className="w-11 h-11 rounded-full bg-red-500 flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                  <Trash2 className="h-5 w-5 text-white" />
                </div>
                <span className="text-white text-[12px] font-medium">{translateInterfaceText("Remover")}</span>
              </button>
            )}
          </div>
          
          <input
            type="file"
            ref={inputRef}
            onChange={handleChange}
            accept={accept}
            className="hidden"
          />
        </div>
      ) : (
        /* Estado vazio - área de upload */
        <div
          className={cn(
            "relative border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group p-8 min-h-[160px]",
            isDragging 
              ? "border-[#1D4FFF] bg-[#1D4FFF]/5 scale-[1.01]" 
              : "border-[#CBD5E1]/60 bg-[#F6F9FF] hover:border-[#1D4FFF]/50 hover:bg-[#EBF5FF]"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => handleClick()}
        >
          {/* Ícone premium */}
          <div className="w-12 h-12 rounded-xl bg-[#1D4FFF]/10 flex items-center justify-center mb-3 transition-transform duration-200 group-hover:scale-105">
            <ImagePlus className="h-5 w-5 text-[#1D4FFF]" strokeWidth={1.5} />
          </div>
          
          {/* Texto hierárquico */}
          <p className="text-[13px] font-medium text-[#1A1A1A] mb-0.5">{label}</p>
          <p className="text-[12px] text-[#6B7280]">
            {translateInterfaceText("Arraste uma imagem ou clique para selecionar")}</p>
          
          {/* Badge de formato */}
          <span className="mt-3 px-3 py-1 text-[10px] font-medium text-[#6B7280] bg-white border border-[#E2E8F0] rounded-full">
            {translateInterfaceText("JPG, PNG • até")}{" "}{maxSize}{translateInterfaceText("MB")}</span>
          
          <input
            type="file"
            ref={inputRef}
            onChange={handleChange}
            accept={accept}
            className="hidden"
          />
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 p-2 bg-[#FEF2F2] border border-[#FECACA] rounded-lg">
          <p className="text-[11px] text-[#DC2626]">{error}</p>
        </div>
      )}
    </div>
  );
};
